import { Plane, Ray, Vector2, Vector2Tuple, Vector3 } from 'three';

import { GroupProps } from '@react-three/fiber';
import { useThree } from '@react-three/fiber';
import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { PolygonControls, useDollhouse, useSelection } from '@spazious/dollhouse-engine';
import { useStaging } from '@spazious/dollhouse-store';
import { useDrag } from '@use-gesture/react';

import { useFeatures } from '../Features/index.js';
const ray = new Ray();

ray.direction.set(0, 0, 1);
// import FLayout from '../FLayout/FLayout.js';
// import FLayout2 from '../FLayout/FLayout2.js';

export const FPolygonHandle: React.FC<{
  onDrag?: (payload: { position: Vector2; first: boolean; last: boolean }) => void;
  onRemove?: () => void;
  color?: string;
  size?: number;
}> = ({ onDrag, onRemove, color = 'white', size = 0.1, ...restProps }) => {
  const { controls, camera, pointer, raycaster } = useThree();

  const bind = useDrag(({ down, first, last }) => {
    const plane = new Plane(new Vector3(0, 1, 0), 0);
    const pos = new Vector3();
    raycaster.setFromCamera(pointer, camera);
    raycaster.ray.intersectPlane(plane, pos);

    onDrag?.({ position: new Vector2(pos.x, pos.z), first: first, last: last });

    if (controls) {
      (controls as any).enabled = !down;
    }
  });
  return (
    <mesh {...(bind() as any)} {...restProps} onDoubleClick={onRemove}>
      <sphereGeometry args={[size, 16, 16]} />
      <meshBasicMaterial color={color} depthTest={false} />
    </mesh>
  );
};

export interface FPolygonProps extends GroupProps {
  points?: Vector2Tuple[];
  fcarpet?: number;
  attrs?: Record<string, any>;
  selector: string;
}

export const FPolygon: React.FC<FPolygonProps> = ({
  points = [],
  selector,
  children,
  ...restProps
}) => {
  const { staging, createAction, update, commit } = useStaging();
  const { select } = useSelection();
  const scene = useDollhouse(({ scene }) => scene);
  const { hasFeature } = useFeatures();
  const disabled = !hasFeature('graphs.setup.node.farea:editable');

  return (
    <group {...restProps}>
      {children}
      {points.length && restProps.visible && (
        <PolygonControls
          disabled={disabled}
          points={points}
          onChange={({ points, type, last }) => {
            switch (type) {
              case 'add':
                staging.add(createAction<[number, number][]>('update', selector, 'points', points));
                staging.commit(CommanderActionTypesEnum.Move);
                break;
              case 'remove':
                staging.add(createAction<[number, number][]>('update', selector, 'points', points));
                staging.commit(CommanderActionTypesEnum.Remove);
                break;
              case 'update':
                update(selector, 'points', points);
                if (last) {
                  commit(CommanderActionTypesEnum.Move);
                }
                break;
            }
          }}
          onClick={() => {
            // TODO: Workaround to get the polygon selected using the userData which contains the polygon node
            const polygonObj = scene.getObjectByProperty('name', 'polygon');
            if (polygonObj && restProps.userData) {
              polygonObj.userData = restProps.userData;
              select([polygonObj]);
            }
          }}
        />
      )}
    </group>
  );
};
